import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, FileText, PenNib, Phone } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "../lib/api";
import { ApplicantMessagePanel } from "../components/ApplicantMessagePanel";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";

const REF_YN = [["kind_character", "Do they know the applicant to be kind and of good character?"],
                ["recommend_vulnerable", "Would they recommend the applicant to care for vulnerable individuals in their own homes, under minimal supervision?"],
                ["positive_reference", "Is this a positive reference?"]];

function PhoneReferenceModal({ aid, r, onClose, onSaved }) {
  const [f, setF] = useState({ relationship: "", years_known: "", kind_character: "", recommend_vulnerable: "",
                               not_fit_reason: "", positive_reference: "", most_important: "", family_attest: false });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const yn = (k, v) => setF((x) => ({ ...x, [k]: x[k] === v ? "" : v }));
  const valid = f.relationship.trim() && f.kind_character && f.recommend_vulnerable && f.positive_reference && f.family_attest;
  const save = async () => {
    setBusy(true);
    try {
      await api.post(`/applicants/${aid}/references/manual`, { ref_name: r.name, ref_phone: r.phone || "", ...f });
      toast.success("Phone reference recorded");
      onSaved();
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black/40 p-4" onClick={onClose} data-testid="phone-reference-modal">
      <div className="mx-auto my-8 max-w-lg rounded-2xl bg-white p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <p className="font-heading text-lg font-semibold text-stone-900">Phone reference — {r.name}</p>
        <p className="mt-0.5 text-xs text-stone-500">Enter the answers from your call — the same six questions the emailed form asks.</p>
        <div className="mt-4 space-y-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs font-medium text-stone-500">How do they know the applicant? *</label>
              <Input value={f.relationship} onChange={set("relationship")} className="border-stone-300" data-testid="phone-ref-relationship" />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-stone-500">Years known</label>
              <Input value={f.years_known} onChange={set("years_known")} className="border-stone-300" data-testid="phone-ref-years" />
            </div>
          </div>
          {REF_YN.map(([k, q]) => (
            <div key={k}>
              <p className="mb-1.5 text-xs font-medium text-stone-700">{q} *</p>
              <div className="flex gap-2">
                {["yes", "no"].map((v) => (
                  <button key={v} type="button" onClick={() => yn(k, v)}
                    className={`rounded-full border px-4 py-1 text-xs font-medium ${f[k] === v ? "border-primary bg-primary/10 text-primary" : "border-stone-300 text-stone-500"}`}
                    data-testid={`phone-ref-${k}-${v}`}>{v === "yes" ? "Yes" : "No"}</button>
                ))}
              </div>
            </div>
          ))}
          <div>
            <label className="mb-1 block text-xs font-medium text-stone-500">Any reason they may not be a good fit?</label>
            <Textarea rows={2} value={f.not_fit_reason} onChange={set("not_fit_reason")} className="border-stone-300" data-testid="phone-ref-not-fit" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-stone-500">Most important thing we should know about them?</label>
            <Textarea rows={2} value={f.most_important} onChange={set("most_important")} className="border-stone-300" data-testid="phone-ref-most-important" />
          </div>
          <label className="flex cursor-pointer items-center gap-2 text-sm text-stone-700" data-testid="phone-ref-family-label">
            <input type="checkbox" checked={f.family_attest} onChange={(e) => setF((x) => ({ ...x, family_attest: e.target.checked }))} data-testid="phone-ref-family" />
            <span className="text-xs">They confirmed on the call they are <b>not related to and not a family member of</b> the applicant. *</span>
          </label>
          <div className="flex gap-2 pt-1">
            <Button variant="outline" onClick={onClose} className="rounded-full border-stone-300" data-testid="phone-ref-cancel">Cancel</Button>
            <Button onClick={save} disabled={busy || !valid} className="rounded-full bg-primary text-primary-foreground" data-testid="phone-ref-save">
              {busy ? "Saving…" : "Save phone reference"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

const Row = ({ label, children }) => (
  <p className="text-sm text-stone-600"><span className="font-medium text-stone-800">{label}:</span> {children || <span className="text-stone-400">—</span>}</p>
);

const Section = ({ title, testid, children }) => (
  <div className="rounded-xl border border-stone-200 bg-white p-5 shadow-sm" data-testid={testid}>
    <p className="font-heading text-sm font-semibold text-stone-900">{title}</p>
    <div className="mt-3 space-y-1.5">{children}</div>
  </div>
);

const objLine = (o) => Object.entries(o || {})
  .filter(([k, v]) => v && !k.startsWith("_") && typeof v !== "object")
  .map(([k, v]) => `${k.replace(/_/g, " ")}: ${v}`).join(" · ");

export default function ApplicationViewPage() {
  const { aid } = useParams();
  const [a, setA] = useState(null);
  const [showMsg, setShowMsg] = useState(false);
  const [phoneRef, setPhoneRef] = useState(null);

  const load = () => api.get(`/applicants/${aid}`).then((r) => setA(r.data)).catch(() => {});
  useEffect(() => { load(); }, [aid]);

  if (!a) return <div className="flex justify-center py-16"><div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" /></div>;

  const address = [a.address, a.city, a.state, a.zip].filter(Boolean).join(", ");
  const grid = a.availability_grid || {};

  return (
    <div className="mx-auto max-w-3xl space-y-5" data-testid="application-view">
      <div>
        <Link to={`/hiring/prescreen/${aid}`} className="inline-flex items-center gap-1 text-xs font-medium text-stone-400 hover:text-primary" data-testid="appview-back-prescreen">
          <ArrowLeft size={13} /> Back to pre-screening
        </Link>
        <div className="mt-1 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="font-heading text-3xl font-bold text-stone-900">{a.name}</h1>
            <p className="mt-1 text-sm text-stone-500">
              {a.position || "Caregiver"} application · submitted {String(a.created_at || "").slice(0, 10)} · status: <span className="font-medium">{a.status}</span>
            </p>
          </div>
          <button onClick={() => setShowMsg((v) => !v)}
            className="inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-2 text-xs font-semibold text-primary-foreground hover:bg-stone-900"
            data-testid="appview-message-btn">
            <PenNib size={14} /> {showMsg ? "Close message" : "Send a message"}
          </button>
        </div>
      </div>

      {showMsg && <ApplicantMessagePanel applicant={a} onSent={load} testid="appview-message" />}

      <Section title="Contact" testid="appview-contact">
        <Row label="Phone"><a className="text-primary hover:underline" href={`tel:${a.phone}`}>{a.phone}</a></Row>
        <Row label="Email"><a className="text-primary hover:underline" href={`mailto:${a.email}`}>{a.email}</a></Row>
        {address && <Row label="Address">{address}</Row>}
        {a.referral_code && <Row label="Referral code">{a.referral_code}</Row>}
      </Section>

      <Section title="Availability" testid="appview-availability">
        <Row label="Available">{(a.availability || []).join(", ")}</Row>
        {Object.keys(grid).length > 0 && (
          <Row label="Grid">{Object.entries(grid).map(([d, parts]) => `${d} (${(parts || []).join(", ")})`).join(" · ")}</Row>
        )}
        {a.availability_notes && <Row label="Notes">{a.availability_notes}</Row>}
      </Section>

      <Section title="Eligibility & requirements" testid="appview-eligibility">
        <Row label="Eligible to work in the US">{a.eligible_us}</Row>
        <Row label="Driver's license + insurance">{a.has_license_insurance}</Row>
        <Row label="Reliable vehicle">{a.reliable_vehicle}</Row>
        <Row label="Lived in PA 2+ years">{a.pa_resident_2y}</Row>
        <Row label="Criminal record question">{a.criminal_attest}{a.criminal_attest_detail ? ` — ${a.criminal_attest_detail}` : ""}</Row>
      </Section>

      {(a.scenario_help || a.why_notice || a.why_reliable || a.why_person) && (
        <Section title="Their answers — in their words" testid="appview-scenarios">
          {a.scenario_help && <Row label="Helped someone when they didn't have to">{a.scenario_help}</Row>}
          {a.why_notice && <Row label="Why noticing small changes matters">{a.why_notice}</Row>}
          {a.why_reliable && <Row label="Why reliability matters">{a.why_reliable}</Row>}
          {a.why_person && <Row label="Tasks vs. caring for a person">{a.why_person}</Row>}
        </Section>
      )}

      {(a.employers || []).length > 0 && (
        <Section title="Work history" testid="appview-employers">
          {a.employers.map((e, i) => <p key={i} className="text-sm text-stone-600">{objLine(e) || "—"}</p>)}
        </Section>
      )}

      {((a.references || []).length > 0 || (a.professional_references || []).length > 0) && (
        <Section title="References" testid="appview-references">
          {(a.references || []).map((r, i) => {
            const done = (a.reference_responses || []).some((x) => (x.ref_name || "").toLowerCase() === (r.name || "").toLowerCase());
            return (
              <div key={i} className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-sm text-stone-600">
                  <span className="font-medium text-stone-800">{r.name}</span> — {r.relationship}
                  {r.phone ? ` · ${r.phone}` : ""}{r.email ? ` · ${r.email}` : ""}
                  {r.may_contact === false && <span className="text-[#C0392B]"> · do not contact</span>}
                  {done && <span className="font-medium text-[#1e8449]"> · completed</span>}
                </p>
                {!done && (
                  <button onClick={() => setPhoneRef(r)}
                    className="inline-flex items-center gap-1 rounded-full border border-stone-300 px-3 py-1 text-[11px] font-medium text-stone-600 hover:border-primary hover:text-primary"
                    data-testid={`appview-phone-ref-${i}`}>
                    <Phone size={12} /> Enter by phone
                  </button>
                )}
              </div>
            );
          })}
          {(a.professional_references || []).map((r, i) => <p key={`p${i}`} className="text-sm text-stone-600">{objLine(r)}</p>)}
        </Section>
      )}
      {phoneRef && <PhoneReferenceModal aid={aid} r={phoneRef} onClose={() => setPhoneRef(null)} onSaved={load} />}

      <Section title="Acknowledgments & signature" testid="appview-signature">
        {a.signature?.typed_name && (
          <>
            <Row label="Signed by">{a.signature.typed_name}{a.signature.date ? ` on ${a.signature.date}` : ""}</Row>
            {a.signature.image && <img src={a.signature.image} alt="Applicant signature" className="mt-2 h-14 rounded border border-stone-200 bg-white" data-testid="appview-signature-img" />}
          </>
        )}
        {a.resume_filename && (
          <Row label="Resume"><span className="inline-flex items-center gap-1"><FileText size={14} /> {a.resume_filename}</span></Row>
        )}
        {(a.acknowledgments || []).length > 0 && <Row label="Acknowledged">{a.acknowledgments.length} items</Row>}
      </Section>
    </div>
  );
}
